import { useState, useRef, useMemo, useCallback } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";

import { Grid } from "@mui/material";
import { Button } from "@gpstrackit/react-controls";
import { NavLink } from "react-router-dom";
//Components
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import CreateEditPackage from 'Modules/packages/.components/createEditPackage';
import DeletePackage from 'Modules/packages/.components/deletePackage';
//Helpers
import { getDateFormatted } from 'Core/data/Helpers';

//Actions
import {
    packagesCreateEditOpenModal,
    packagesDeletePackageOpenModal,
} from 'Redux/actions';

//Notification
import { NotificationManager } from "Components/Notifications";

//Permission
import { HasPermission } from "Core/security"

const PackagesTab = (props) => {
    const { paramDealer, dealerId, height } = props;
    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    const [columns, setColumns] = useState([
        {
            key: "name",
            field: "name",
            title: messages['packages_tab_name'],
            width: 200,
            show: true,
            sort: true,
            locked: true,
            resize: true,
            export: true,
            functionString: 'return row?.name',
            code: "packages_tab_name",
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <NavLink to={`/app/packages/overview/${item?.id}`} style={{ color: 'black' }}>
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.name}</span>
                            </NavLink>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "projectId",
            sort: true,
            field: "projectId",
            title: messages['packages_tab_project'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.projectId',
            code: "packages_tab_project",
            render: (item) => {
                return <span>{item?.projectId}</span>
            }
        },
        {
            key: "assignedCount",
            field: "assignedCount",
            title: messages['packages_tab_assigned'],
            width: 350,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.assignedCount',
            code: "packages_tab_assigned",
            render: (item) => {
                return <span>{item?.assignedCount}</span>
            }
        },
        {
            key: "createdOn",
            field: "createdOn",
            title: messages['packages_tab_created_at'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            sort: true,
            export: true,
            functionString:
                `
            function formatDate(date) {
                let dateToformat = moment(date)
                let formatted = moment(dateToformat).format("YYYY/MM/DD HH:mm:ss")
                return formatted;
            }
            return row?.createdOn ? formatDate(row?.createdOn) : ''
            `,
            code: "packages_tab_created_at",
            render: (item) => {
                return <span>{item?.createdOn && getDateFormatted(item?.createdOn, "date", "datetime")}</span>
            }
        },
    ]);
    const [searchFields] = useState(["name", "projectId", "company_name"]);
    const [selectedItems, setSelectedItems] = useState([]);

    const openModalEditPackage = useSelector(state => state.createEditPackageRedux.openModal);
    const openModalDeletePackage = useSelector(state => state.deletePackageRedux.openModal);
    const user = useSelector(state => state.securityRedux.user);

    // =============================================================================
    // GROUPED CONFIGURATION OBJECTS
    // =============================================================================

    const graphqlConfig = useMemo(() => ({
        function: "searchRoles",
        sortType: "RoleSortInput",
        conditionsType: "RoleSearchInput",
        customQuery: "id",
        dynamicQuery: true,
    }), []);

    const searchConfig = useMemo(() => ({
        asyncSelect: true,
        conditions: ["name", "projectId", "company_name"],
        optionValue: "id",
        optionLabel: "name",
        customSearch: [
            { fields: ["isCustom"], terms: [false], exact: true },
            { fields: ["companyId"], terms: ['NULL'], exact: true }
        ],
    }), []);

    const uiConfig = useMemo(() => ({
        hideFilters: true,
        hideSplit: true,
        topHeight: props?.single ? 286 : 200,
        height: height && height + "px" || "500px",
        hideAllInPagination: true,
    }), [height]);

    const exportConfig = useMemo(() => ({
        fileName: "packages-tab",
    }), []);

    // =============================================================================
    // EXTRACTED HANDLERS
    // =============================================================================

    const handleChangeConditions = useCallback((tags, conditions, total, newColumns, rows, sort) => {
        //current conditions
        let currentConditions = {}
        let currentTags = {}
        if (!conditions && tags) {
            currentConditions = tableRef?.current?.conditions;
            currentTags = tags;
        } else {
            currentConditions = conditions || [];
            currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
            if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
        }

        currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'name' && item?.fields[0] !== 'isCustom' && item?.fields[0] !== 'companyId' && item?.fields[0] !== 'dealerId');

        let tagsById = currentTags.filter((item) => typeof item.id === "number");
        if (tagsById?.length > 0) {
            //New Items
            let currentIds = tagsById.map((item) => (item.id));
            if (currentIds.length > 0) {
                currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
            }
        }
        let tagsByLabel = currentTags.filter(item => typeof item.id === "string")
        if (tagsByLabel.length > 0) {
            let currentLabels = tagsByLabel.map((item) => (item.name))
            if (currentLabels.length > 0) {
                currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
            }
        }

        currentConditions && currentConditions.push({ fields: ["isCustom"], terms: [false], exact: true, conditionOperator: "AND" });
        currentConditions && currentConditions.push({ fields: ["companyId"], terms: ['NULL'], exact: true, conditionOperator: "AND" });
        currentConditions && currentConditions.push({ fields: ["dealerId"], terms: [dealerId], exact: true, conditionOperator: "AND" });

        if (!conditions && tags) {
            tableRef?.current?.getData({ newConditions: currentConditions, newColumns });
        } else {
            tableRef?.current?.getData({ newRows: rows, newSort: sort, newConditions: currentConditions });
        }
    }, [dealerId, searchFields]);

    const handleChangeColumns = useCallback((items) => {
        setColumns(items);
    }, []);

    const handleClickMenu = useCallback((event, item) => {
        if (event === messages['packages_tab_edit']) {
            if (HasPermission(user, "rol", "u")) {
                dispatch(packagesCreateEditOpenModal(item?.id))
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['packages_tab_edit'],
                    5000,
                );
            }
        }
        if (event === messages['delete']) {
            if (HasPermission(user, "rol", "d")) {
                dispatch(packagesDeletePackageOpenModal(item?.id))
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['delete'],
                    5000,
                );
            }
        }
    }, [messages, user, dispatch]);

    const handleSelectedItems = useCallback((items) => {
        setSelectedItems(items);
    }, []);

    const menuOptions = useCallback(() => {
        return [
            messages['packages_tab_edit'],
            messages['delete'],
        ]
    }, [messages]);

    const options = useMemo(() => [
        {
            id: "edit-package",
            label: messages['packages_tab_edit'],
            show: true,
            disabled: !HasPermission(user, "rol", "u"),
            single: true,
            action: () => {
                dispatch(packagesCreateEditOpenModal(selectedItems[0].id))
            }
        },
        {
            id: "delete-package",
            label: messages['delete'],
            show: true,
            disabled: !HasPermission(user, "rol", "d"),
            single: true,
            action: () => {
                dispatch(packagesDeletePackageOpenModal(selectedItems[0].id))
            }
        }
    ], [messages, user, selectedItems, dispatch]);

    const extraButton = useCallback(() => {
        return (
            <div style={{ margin: '0 5px' }}>
                <Button
                    className="btn-dark-blue"
                    disabled={!HasPermission(user, "rol", "c")}
                    onClick={(e) => {
                        e.preventDefault();
                        dispatch(packagesCreateEditOpenModal());
                    }}
                >
                    {messages['packages_tab_add_packages']}
                </Button>
            </div>
        )
    }, [user, dispatch, messages]);

    return (
        <div className="packages-tab" style={{ marginTop: 10 }}>
            <DataViewInfiniteScroll
                ref={tableRef}
                name="pm-packages-tab-v1"
                entity="packages-tab"
                columns={columns}
                // Grouped configs
                graphqlConfig={graphqlConfig}
                searchConfig={searchConfig}
                uiConfig={uiConfig}
                exportConfig={exportConfig}
                // Handlers
                onChangeConditions={handleChangeConditions}
                searchFields={searchFields}
                onChangeColumns={handleChangeColumns}
                tableButtons={["export", 'orderColumn']}
                options={options}
                menuOptions={menuOptions}
                onClickMenu={handleClickMenu}
                onSelectedItems={handleSelectedItems}
                extraButton={extraButton}
            />
            {openModalEditPackage && <CreateEditPackage tableRef={tableRef} paramDealer={paramDealer} />}
            {openModalDeletePackage && <DeletePackage tableRef={tableRef} />}
        </div>
    )
}

export default PackagesTab;
